import { ReactComponent as CheckIcon } from "../../assets/icons/solid/check-circle.svg";
import { ReactComponent as CrossIcon } from "../../assets/icons/solid/cross-circle.svg";
import { UserLimit } from "../../models/enumerations/user-limit";
import { LicenseLimit } from "../../models/enumerations/license-limit";
import { useTranslation } from 'react-i18next';
import Accordion from "../accordion/accordion";
import { Dispatch, SetStateAction } from "react";
import { PaymentCycle } from "../../models/enumerations/payment-cycle";
import GenericTabs from "../generic-tabs/generic-tabs";
import { FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { PlanType } from "../../models/enumerations/plan-type";
import { DocumentData } from "firebase/firestore";
import { Button } from "../button/button";

enum SubscriptionCarouselIndex {
    Trial,
    Core,
    Pro,
    Enterprise,
}

interface PlanData {
    planType: PlanType;
    carouselIndex: SubscriptionCarouselIndex;
    isCurrent: boolean;
    label: string;
    description: string;
    color: string;
    price: {
        [PaymentCycle.Monthly]: string;
        [PaymentCycle.Quarterly]: string;
        [PaymentCycle.Yearly]: string;
    };
    numberUsers: string;
    buttonText: string;
    buttonTextTable?: string;
    onButtonClick?: () => void;
    productToSelect?: DocumentData;
    benefitsTitle: string;
    benefits: {
        icon: React.ReactElement;
        text: string;
    }[];
}

interface PlansTableContentProps {
    carouselItemIndex: number;
    setCarouselItemIndex: Dispatch<SetStateAction<number>>;
    paymentCycle: PaymentCycle;
    setPaymentCycle: Dispatch<SetStateAction<PaymentCycle>>;
    addOnPrices?: { user?: string, license?: string, organization?: string };
    planDataArr: (PlanData | undefined)[];
    selectProduct: Function;
    isMobile: boolean;
    isUpgrade: boolean;
}

const COMPONENT_CLASS = "c-subscriptions";

const PlansTableContent: React.FC<PlansTableContentProps> = (props) => {
    const {
        paymentCycle,
        setPaymentCycle,
        addOnPrices,
        planDataArr,
        selectProduct,
        isMobile,
        isUpgrade
    } = props;

    let {
        carouselItemIndex,
        setCarouselItemIndex,
    } = props;

    const { t } = useTranslation();

    if (isUpgrade) {
        carouselItemIndex += 1;
    }

    const handleSetCarouselItemIndex = (index: number) => {
        if (isUpgrade) {
            index -= 1;
        }

        setCarouselItemIndex(index);
    };

    const selectedPlanData = planDataArr.find((pd) => pd?.carouselIndex === carouselItemIndex);

    const tabs = [
        {
            id: SubscriptionCarouselIndex.Core,
            label: t('general.plan.core'),
            onClick: () => handleSetCarouselItemIndex(SubscriptionCarouselIndex.Core)
        },
        {
            id: SubscriptionCarouselIndex.Pro,
            label: t('general.plan.pro'),
            onClick: () => handleSetCarouselItemIndex(SubscriptionCarouselIndex.Pro)
        },
        {
            id: SubscriptionCarouselIndex.Enterprise,
            label: t('general.plan.enterprise'),
            onClick: () => handleSetCarouselItemIndex(SubscriptionCarouselIndex.Enterprise)
        },
    ];


    if (!isUpgrade) {
        tabs.unshift({
            id: SubscriptionCarouselIndex.Trial,
            label: t('general.plan.trial'),
            onClick: () => handleSetCarouselItemIndex(SubscriptionCarouselIndex.Trial)
        });
    }

    const checkmarkElement = <CheckIcon style={{ fill: "#00DFBC", color: "white" }} />;
    const crossElement = <CrossIcon />;

    const tableContent = [
        {
            title: t("pricing.plansPage.tableContent.basics.title"),
            rows: [
                {
                    name: t('pricing.plansPage.tableContent.basics.users'),
                    tooltip: t('pricing.plansPage.tableContent.basics.users_tooltip'),
                    videoUrl: t('pricing.plansPage.tableContent.basics.users_video'),
                    [SubscriptionCarouselIndex.Trial]: UserLimit.Trial,
                    [SubscriptionCarouselIndex.Core]: UserLimit.Core,
                    [SubscriptionCarouselIndex.Pro]: UserLimit.Pro,
                    [SubscriptionCarouselIndex.Enterprise]: t('pricing.general.custom'),
                },
                {
                    name: t('pricing.plansPage.tableContent.basics.wellnessProgram'),
                    tooltip: t('pricing.plansPage.tableContent.basics.wellnessProgram_tooltip'),
                    videoUrl: t('pricing.plansPage.tableContent.basics.wellnessProgram_video'),
                    [SubscriptionCarouselIndex.Trial]: LicenseLimit.Trial,
                    [SubscriptionCarouselIndex.Core]: LicenseLimit.Core,
                    [SubscriptionCarouselIndex.Pro]: LicenseLimit.Pro,
                    [SubscriptionCarouselIndex.Enterprise]: t('pricing.general.custom'),
                },
                {
                    name: t('pricing.plansPage.tableContent.basics.course'),
                    tooltip: t('pricing.plansPage.tableContent.basics.course_tooltip'),
                    videoUrl: t('pricing.plansPage.tableContent.basics.course_video'),
                    [SubscriptionCarouselIndex.Trial]: crossElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement,
                },
                {
                    name: t('pricing.plansPage.tableContent.basics.marketing'),
                    tooltip: t('pricing.plansPage.tableContent.basics.marketing_tooltip'),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement,
                },
                {
                    name: t('pricing.plansPage.tableContent.basics.multipleOrgs'),
                    tooltip: t('pricing.plansPage.tableContent.basics.multipleOrgs_tooltip'),
                    [SubscriptionCarouselIndex.Trial]: crossElement,
                    [SubscriptionCarouselIndex.Core]: crossElement,
                    [SubscriptionCarouselIndex.Pro]: t('pricing.general.comingSoon'),
                    [SubscriptionCarouselIndex.Enterprise]: t('pricing.general.comingSoon'),
                },
                {
                    name: t('pricing.plansPage.tableContent.basics.support'),
                    tooltip: t('pricing.plansPage.tableContent.basics.support_tooltip'),
                    [SubscriptionCarouselIndex.Trial]: crossElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement,
                },
                {
                    name: t('pricing.plansPage.tableContent.basics.manager'),
                    tooltip: t('pricing.plansPage.tableContent.basics.manager_tooltip'),
                    [SubscriptionCarouselIndex.Trial]: crossElement,
                    [SubscriptionCarouselIndex.Core]: crossElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement,
                },
            ]
        },
        {
            title: t("pricing.plansPage.tableContent.wellnessPlatform.title"),
            videoUrl: t("pricing.plansPage.tableContent.wellnessPlatform.video"),
            rows: [
                {
                    name: t("pricing.plansPage.tableContent.wellnessPlatform.retests"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPlatform.retests_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: t("pricing.general.unlimited"),
                    [SubscriptionCarouselIndex.Core]: t("pricing.general.unlimited"),
                    [SubscriptionCarouselIndex.Pro]: t("pricing.general.unlimited"),
                    [SubscriptionCarouselIndex.Enterprise]: t("pricing.general.unlimited")
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPlatform.dashboard"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPlatform.dashboard_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPlatform.structureManagement"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPlatform.structureManagement_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPlatform.userProfiles"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPlatform.userProfiles_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPlatform.tracking"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPlatform.tracking_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPlatform.groupsTags"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPlatform.groupsTags_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: t("pricing.general.unlimited"),
                    [SubscriptionCarouselIndex.Core]: t("pricing.general.unlimited"),
                    [SubscriptionCarouselIndex.Pro]: t("pricing.general.unlimited"),
                    [SubscriptionCarouselIndex.Enterprise]: t("pricing.general.unlimited")
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPlatform.reporting"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPlatform.reporting_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPlatform.assessmentLinks"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPlatform.assessmentLinks_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: t("pricing.general.unlimited"),
                    [SubscriptionCarouselIndex.Core]: t("pricing.general.unlimited"),
                    [SubscriptionCarouselIndex.Pro]: t("pricing.general.unlimited"),
                    [SubscriptionCarouselIndex.Enterprise]: t("pricing.general.unlimited")
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPlatform.assessmentResults"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPlatform.assessmentResults_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPlatform.integrations"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPlatform.integrations_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPlatform.csvExports"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPlatform.csvExports_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
            ]
        },
        {
            title: t("pricing.plansPage.tableContent.wellnessPrograms.title"),
            videoUrl: t("pricing.plansPage.tableContent.wellnessPrograms.video"),
            rows: [
                {
                    name: t("pricing.plansPage.tableContent.wellnessPrograms.wellnessScore"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPrograms.wellnessScore_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPrograms.detailedReports"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPrograms.detailedReports_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPrograms.assessmentResults"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPrograms.assessmentResults_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPrograms.areasOfFocus"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPrograms.areasOfFocus_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPrograms.personalizedPlans"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPrograms.personalizedPlans_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPrograms.movementExercises"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPrograms.movementExercises_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPrograms.behaviorEducation"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPrograms.behaviorEducation_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPrograms.riskRecommendations"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPrograms.riskRecommendations_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPrograms.discoverContent"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPrograms.discoverContent_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: t("pricing.general.comingSoon"),
                    [SubscriptionCarouselIndex.Core]: t("pricing.general.comingSoon"),
                    [SubscriptionCarouselIndex.Pro]: t("pricing.general.comingSoon"),
                    [SubscriptionCarouselIndex.Enterprise]: t("pricing.general.comingSoon")
                },
                {
                    name: t("pricing.plansPage.tableContent.wellnessPrograms.multiLanguage"),
                    tooltip: t("pricing.plansPage.tableContent.wellnessPrograms.multiLanguage_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: checkmarkElement,
                    [SubscriptionCarouselIndex.Core]: checkmarkElement,
                    [SubscriptionCarouselIndex.Pro]: checkmarkElement,
                    [SubscriptionCarouselIndex.Enterprise]: checkmarkElement
                }
            ]
        },
        {
            title: t("pricing.plansPage.tableContent.addOns.title"),
            rows: [
                {
                    name: t("pricing.plansPage.tableContent.addOns.users"),
                    tooltip: t("pricing.plansPage.tableContent.addOns.users_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: crossElement,
                    [SubscriptionCarouselIndex.Core]: `${t('pricing.general.addOnPrice_month', { price: addOnPrices?.user || "..." })}`,
                    [SubscriptionCarouselIndex.Pro]: `${t('pricing.general.addOnPrice_month', { price: addOnPrices?.user || "..." })}`,
                    [SubscriptionCarouselIndex.Enterprise]: t('pricing.general.customPricing')
                },
                {
                    name: t("pricing.plansPage.tableContent.addOns.wellnessPrograms"),
                    tooltip: t("pricing.plansPage.tableContent.addOns.wellnessPrograms_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: crossElement,
                    [SubscriptionCarouselIndex.Core]: `${t('pricing.general.addOnPrice_month', { price: addOnPrices?.license || "..." })}`,
                    [SubscriptionCarouselIndex.Pro]: `${t('pricing.general.addOnPrice_month', { price: addOnPrices?.license || "..." })}`,
                    [SubscriptionCarouselIndex.Enterprise]: t('pricing.general.customPricing')
                },
                {
                    name: t("pricing.plansPage.tableContent.addOns.subOrgs"),
                    tooltip: t("pricing.plansPage.tableContent.addOns.subOrgs_tooltip"),
                    [SubscriptionCarouselIndex.Trial]: crossElement,
                    [SubscriptionCarouselIndex.Core]: t('pricing.general.comingSoon'),
                    [SubscriptionCarouselIndex.Pro]: t('pricing.general.comingSoon'),
                    [SubscriptionCarouselIndex.Enterprise]: t('pricing.general.comingSoon')
                },
            ]
        }
    ];

    return (
        <div className="pt-6">
            <h2 className="text-center">{t('pricing.plansPage.tableContent.title')}</h2>

            <table className={`${COMPONENT_CLASS}__plans-table md:mb-4 my-6`}>
                <thead>
                    <tr>
                        <th className={`${COMPONENT_CLASS}__plans-table-header`}>
                            <FormControl>
                                <RadioGroup
                                    defaultValue="monthly"
                                    name="radio-buttons-payment-cycle"
                                >
                                    <div className={`${COMPONENT_CLASS}__plans-table-header__radio-container`}>
                                        <div className={`pl-4 rounded-xl h-9 flex items-center ${paymentCycle === PaymentCycle.Monthly ? "bg-black-gray-5" : ""}`}>
                                            <FormControlLabel
                                                value={PaymentCycle.Monthly}
                                                control={<Radio size="small" sx={{ marginRight: "0.25rem" }} />}
                                                label={<span style={{ fontSize: '12px' }}>{t('general.paymentCycle.monthly')}</span>}
                                                checked={paymentCycle === PaymentCycle.Monthly}
                                                onChange={(event, checked) => {
                                                    if (checked) {
                                                        setPaymentCycle(PaymentCycle.Monthly)
                                                    }
                                                }}
                                            />
                                        </div>

                                        <div className={`pl-4 rounded-xl h-9 flex items-center ${paymentCycle === PaymentCycle.Yearly ? "bg-black-gray-5" : ""}`}>
                                            <FormControlLabel
                                                value={PaymentCycle.Yearly}
                                                control={<Radio size="small" sx={{ marginRight: "0.25rem" }} />}
                                                label={<span style={{ fontSize: '12px' }}>{t('general.paymentCycle.yearly')}</span>}
                                                checked={paymentCycle === PaymentCycle.Yearly}
                                                onChange={(event, checked) => {
                                                    if (checked) {
                                                        setPaymentCycle(PaymentCycle.Yearly)
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </RadioGroup>
                            </FormControl>
                        </th>

                        {!isMobile &&
                            planDataArr.map((pd) => {
                                if (pd) {
                                    return (
                                        <th className="px-6" key={`product-card-small-${pd.planType}`}>
                                            <div className="flex flex-col text-center items-center gap-2">
                                                <div className="w-full rounded-t-xl h-2" style={{ backgroundColor: pd.color }}></div>
                                                <div className="w-full">
                                                    <h3 className="m-0 text-center">{pd.label}</h3>
                                                    <div className="text-xs font-light text-black-gray-50">
                                                        {pd.price[paymentCycle]}
                                                        {paymentCycle === PaymentCycle.Monthly &&
                                                            pd.planType !== PlanType.Enterprise &&
                                                            pd.planType !== PlanType.Trial
                                                            ?
                                                            t('pricing.general.month')
                                                            :
                                                            pd.planType !== PlanType.Enterprise &&
                                                            pd.planType !== PlanType.Trial &&
                                                            t('pricing.general.year')
                                                        }
                                                    </div>
                                                </div>
                                                <div>
                                                    <Button
                                                        type="table-action"
                                                        disabled={pd.isCurrent}
                                                        buttonText={pd.buttonTextTable ? pd.buttonTextTable : pd.buttonText}
                                                        onClick={() => {
                                                            if (pd.onButtonClick) {
                                                                pd.onButtonClick();
                                                            }
                                                            else if (pd.productToSelect) {
                                                                selectProduct(pd.productToSelect);
                                                            }
                                                        }} />
                                                </div>
                                            </div>
                                        </th>
                                    )
                                }

                                return <></>;
                            })
                        }
                    </tr>
                </thead>
            </table>

            {isMobile &&
                <>
                    <GenericTabs tabs={tabs}
                        initialTab={carouselItemIndex}
                        currentTab={carouselItemIndex}
                    />

                    {selectedPlanData &&
                        <div className="py-6 flex flex-col items-center justify-center">
                            <h3 className="mb-2 flex gap-1">
                                <span>{selectedPlanData.price[paymentCycle]}</span>
                                <span>
                                    {paymentCycle === PaymentCycle.Monthly &&
                                        selectedPlanData.planType !== PlanType.Enterprise &&
                                        selectedPlanData.planType !== PlanType.Trial
                                        ?
                                        t('pricing.general.month')
                                        :
                                        selectedPlanData.planType !== PlanType.Enterprise &&
                                        selectedPlanData.planType !== PlanType.Trial &&
                                        t('pricing.general.year')
                                    }
                                </span>
                            </h3>
                            <div>
                                <Button
                                    type="default"
                                    buttonText={selectedPlanData.buttonTextTable ? selectedPlanData.buttonTextTable : selectedPlanData.buttonText}
                                    onClick={() => {
                                        if (selectedPlanData.onButtonClick) {
                                            selectedPlanData.onButtonClick();
                                        }
                                        else if (selectedPlanData.productToSelect) {
                                            selectProduct(selectedPlanData.productToSelect);
                                        }
                                    }} />
                            </div>
                        </div>
                    }
                </>
            }

            {
                tableContent.map((table, index: number) =>
                    <div key={`plan-table-${index}`} className="mb-4">
                        <Accordion
                            isAccordionOpen={true}
                            title={table.title}
                            content={
                                <table className={`${COMPONENT_CLASS}__plans-table`}>
                                    <tbody>
                                        {table.rows.map((row, i: number) => (
                                            <tr
                                                className={`${COMPONENT_CLASS}__item `}
                                                key={`user-list-${i}`}
                                            >
                                                <td style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', paddingTop: '1rem', paddingBottom: '1rem', fontSize: '12px' }}>
                                                    <div style={{ width: '200px' }}>
                                                        <div className="tooltip">
                                                            <span className="tooltiptext">{row.tooltip}</span>
                                                            {row.name}
                                                        </div>
                                                    </div>

                                                    {isMobile
                                                        ?
                                                        <div className={`${COMPONENT_CLASS}__row `}>
                                                            {row[carouselItemIndex as SubscriptionCarouselIndex]}
                                                        </div>
                                                        :
                                                        <>
                                                            {!isUpgrade &&
                                                                <div className={`${COMPONENT_CLASS}__row `}>
                                                                    {row[SubscriptionCarouselIndex.Trial]}
                                                                </div>
                                                            }
                                                            <div className={`${COMPONENT_CLASS}__row `}>
                                                                {row[SubscriptionCarouselIndex.Core]}
                                                            </div>
                                                            <div className={`${COMPONENT_CLASS}__row `}>
                                                                {row[SubscriptionCarouselIndex.Pro]}
                                                            </div>
                                                            <div className={`${COMPONENT_CLASS}__row `}>
                                                                {row[SubscriptionCarouselIndex.Enterprise]}
                                                            </div>
                                                        </>
                                                    }
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            }
                        />
                    </div >
                )
            }
        </div>
    );
}

export default PlansTableContent;